import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";
import { Marquee } from "@/components/Marquee";

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const line = {
  hidden: { y: "115%" },
  show: (i = 0) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const TRUST = [
  "Over 20 years of combined construction and business experience",
  "Disciplined project management and clear communication",
  "Delivered on time, on budget, and to a high standard of quality",
  "Experience with occupied, phased, and multi-site work",
];

export default function Home() {
  const { content } = useContent();
  const hero = content.hero || {};
  const stats = content.stats || [];
  const services = content.services || [];
  const about = content.about || {};

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section ref={heroRef} className="relative min-h-[92vh] flex items-center overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
          <img src={hero.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gray-950/70" />
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full py-28">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="flex items-center gap-3 mb-7">
            <span className="h-px w-12 bg-gray-400" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-400">{hero.label}</span>
          </motion.div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[0.98]">
            <span className="block overflow-hidden">
              <motion.span className="block" initial="hidden" animate="show" custom={0} variants={line}>{hero.title_line1}</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block" initial="hidden" animate="show" custom={1} variants={line}>{hero.title_line2}</motion.span>
            </span>
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-7 text-base sm:text-lg text-gray-300 max-w-xl leading-relaxed">
            {hero.subtext}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.78, duration: 0.7 }}
            className="mt-9 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="hero-cta"
              className="group inline-flex items-center gap-2 bg-white text-gray-950 font-semibold px-7 py-4 rounded-sm hover:bg-gray-200 transition-colors duration-200">
              Request a Project Consultation
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
            <Link to="/services" data-testid="hero-services-link"
              className="inline-flex items-center gap-2 border border-white/40 text-white font-semibold px-7 py-4 rounded-sm hover:bg-white hover:text-gray-950 transition-colors duration-200">
              View Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Marquee */}
      <Marquee items={["Tenant Improvements", "Interior Buildouts", "Renovations", "Facility Upgrades", "Preconstruction", "Multi-Site"]} />

      {/* Stats */}
      <section className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-200">
          {stats.map((s, i) => (
            <motion.div key={i} data-testid={`stat-${i}`} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              className="py-10 sm:py-12 sm:px-8 first:sm:pl-0">
              <div className="font-heading text-4xl sm:text-5xl font-bold text-gray-900">{s.value}</div>
              <div className="mt-2 text-sm text-gray-500 uppercase tracking-wider">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Services overview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gray-300" />
              <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">What We Do</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-gray-900 max-w-xl">
              A single team for every phase of commercial construction.
            </h2>
          </div>
          <Link to="/services" data-testid="services-overview-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-500 transition-colors duration-200 whitespace-nowrap">
            All Services <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 6).map((s, i) => (
            <motion.div key={s.id} data-testid={`home-service-${s.id}`} initial="hidden" whileInView="show"
              viewport={{ once: true, amount: 0.2 }} custom={i} variants={fadeUp}
              className="group border border-gray-200 bg-white overflow-hidden hover:border-gray-900 transition-colors duration-300">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={s.image} alt={s.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[filter,transform] duration-500" />
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-semibold text-gray-900">{s.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Trust band */}
      <section className="bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-2">
          <div className="py-20 md:pr-14 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-gray-300" />
              <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">Why Mountain Core</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">
              Built for business. Built to last.
            </h2>
            <ul className="mt-8 space-y-4">
              {TRUST.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle size={22} weight="fill" className="text-gray-900 shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700 leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/about" data-testid="home-about-link"
              className="mt-9 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-500 transition-colors duration-200">
              Meet the team <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
          <div className="relative min-h-[320px] md:min-h-full overflow-hidden">
            <img src={about.image1} alt="Our team on site" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-gray-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              Have a project in mind?
            </h2>
            <p className="mt-3 text-gray-400 max-w-lg">
              Tell us about your space and timeline. We will review your request and respond within 1-2 business days.
            </p>
          </div>
          <Link to="/contact" data-testid="cta-band-button"
            className="group inline-flex items-center gap-2 bg-white text-gray-950 font-semibold px-7 py-4 rounded-sm hover:bg-gray-200 transition-colors duration-200 whitespace-nowrap">
            Request a Consultation
            <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-200" />
          </Link>
        </div>
      </section>
    </div>
  );
}
